import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * ### メッセージ送信の返信 API 操作クラス
 */
export default class SendMessageReplies {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * GET SendMessageReplies
   */
  public async get(userStore: LoginUser, datetime: number): Promise<Array<SendMessageReplyModel>> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      datetime: datetime
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendMessageReplies, formatParams);
    const response = await this.commonService.executeGet<Array<SendMessageReplyModel>>(userStore, requestUrl, this.constructor.name, null, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * GET SendMessageRepliesFacilityUnread
   */
  public async getUnreadInFacility(userStore: LoginUser): Promise<SendMessageRepliesUnreadModel> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendMessageRepliesUnreadInFacility, formatParams);
    const response = await this.commonService.executeGet<SendMessageRepliesUnreadModel>(userStore, requestUrl, this.constructor.name, null, HitomeqErrorType.IGNORE, 0);

    return response.data;
  }

  /**
   * GET SendMessageRepliesUnread
   */
  public async getUnread(userStore: LoginUser, datetime: number): Promise<SendMessageRepliesUnreadModel> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      datetime: datetime
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendMessageRepliesUnread, formatParams);
    const response = await this.commonService.executeGet<SendMessageRepliesUnreadModel>(userStore, requestUrl, this.constructor.name, null, HitomeqErrorType.IGNORE, 0);

    return response.data;
  }

  /**
   * POST SendMessageReplies
   */
  public async post(userStore: LoginUser, parentMessagesSortKey: string, residentId: number, familyId: number, requestBody: SendMessageRepliesPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      parentMessagesSortKey: parentMessagesSortKey,
      residentId: residentId,
      familyId: familyId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendMessageReplyToFamily, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * POST SendMessageReplies
   */
  public async postEMail(userStore: LoginUser, parentMessagesSortKey: string, residentId: number, familyId: number, requestBody: SendMessageRepliesPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      parentMessagesSortKey: parentMessagesSortKey,
      residentId: residentId,
      familyId: familyId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendEMailReplyToFamily, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

/**
 * ### メッセージ送信の返信
 */
export interface SendMessageReplyModel {
  /** タイムスタンプ（yyyymmddhhMMssSSS） */
  datetime: number;
  /** 施設 ID */
  facilityId: number;
  /** 送信元メッセージソートキー */
  parentMessagesSortKey: string;
  /** 利用者 ID */
  residentId: number;
  /** 家族 ID */
  familyId: number;
  /** 返信内容のテキスト */
  message: string;
  /** 返信を読んだ時間リスト */
  readDatetimes: number[];
  /** 返信を読んだログインユーザー名リスト */
  readUserNames: string[];
  /** 返信を読んだログインユーザーIDリスト */
  readUserIds: string[];
  /** 利用者名 */
  residentName: string;
  /** 利用者名（ふりがな） */
  residentKanaName: string;
  /** 利用者部屋名 */
  residentRoom: string;
  /** 家族名 */
  familyName: string;
  /** 家族電話番号 */
  familyTel: string;
  /** 家族役割 */
  familyPosition: string;
  /** 家族続柄 */
  familyRelation: string;
  /** レコード更新日 */
  updated: number;
  /** メッセージ送信の種別 */
  type: string;
  /** 送信種別(0:家族、1:施設) */
  senderType: SendMessageReplySenderType;
}

/**
 * ### メッセージ送信の種別
 */
export enum SendMessageReplyType {
  /** 返信 */
  REPLY = "reply",
  /** 送信エラー */
  ERROR = "error"
}

export enum SendMessageReplySenderType {
  FAMILY = 0,
  FACILITY = 1
}

/**
 * ### 施設のメッセージ送信の返信未読が存在するか否か
 */
export interface SendMessageRepliesUnreadModel {
  /** 未読の返信メッセージがあるか否か */
  unread: boolean;
}

export interface SendMessageRepliesPostModel {
  message: string;
}
